import { FormattedMessage, useIntl } from 'react-intl';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { PatientDataSet } from '~/domain/dicom/patientdataset';
import { calculateAge, formatDateCompact } from '~/utils/date';
import PropertiesPanel from './PropertiesPanel';

interface PatientPropertiesProps {
    dataSet: PatientDataSet;
    showTitle?: boolean;
}

const sexMessageIds: Record<string, string> = {
    M: 'sex.male',
    F: 'sex.female',
    O: 'sex.other',
};

export default function PatientProperties({
    dataSet,
    showTitle = true,
}: PatientPropertiesProps) {
    const intl = useIntl();
    const dash = '\u2014';

    const patientName = dataSet.PatientName || dash;
    const patientId = dataSet.PatientID || dash;

    const sexCode = dataSet.PatientSex;
    const patientSex = sexCode
        ? intl.formatMessage({
              id: sexMessageIds[sexCode.toUpperCase()] || 'sex.unknown',
          })
        : undefined;

    const rawDob = dataSet.PatientBirthDate;
    const formattedDob = rawDob ? formatDateCompact(rawDob) : undefined;
    const patientAge = rawDob ? calculateAge(rawDob) : undefined;

    return (
        <PropertiesPanel titleId="patientInfo" showTitle={showTitle}>
            <LabelText>
                <FormattedMessage id="name" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>{patientName}</SensitiveInfoTypography>
            </ValueText>

            <LabelText>
                <FormattedMessage id="patientId" />
            </LabelText>
            <ValueText component="span">
                <SensitiveInfoTypography>{patientId}</SensitiveInfoTypography>
            </ValueText>

            {patientSex && (
                <>
                    <LabelText>
                        <FormattedMessage id="sex" />
                    </LabelText>
                    <ValueText component="span">
                        <SensitiveInfoTypography>
                            {patientSex}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}

            {formattedDob && (
                <>
                    <LabelText>
                        <FormattedMessage id="dob" />
                    </LabelText>
                    <ValueText component="span">
                        <SensitiveInfoTypography>
                            {formattedDob}
                            {patientAge !== undefined &&
                                ` (${intl.formatMessage({ id: 'yearsOld' }, { age: patientAge })})`}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}
        </PropertiesPanel>
    );
}
